import { FC, useCallback, useEffect } from 'react';
import { RouteComponentProps } from 'react-router';
import { useSelector } from 'react-redux';

import { selectIsEmbeddedMessagingCoreFeatureFlagOn } from 'core/userContext';
import {
  EmbeddedMessage,
  EmbeddedMessageMedium,
  WhatsApp,
  WhatsAppMedium,
} from 'constants/messageMediums';
import { MessageMedium } from 'modules/graphql/types';
import { useWhatsAppMessageChannelFeature } from 'utils/hooks/featureFlags';
import { useQueryParams } from 'utils/hooks/useQueryParams';
import { DisplayContent } from './common/content';
import {
  TemplateCorruptedError,
  TemplateNotFoundError,
  TemplateViewPermissionError,
} from './common/TemplateEditorErrors';
import { EditorLoader } from './common/EditorLoader';
import {
  GqlTemplateResponseTypes,
  MessagingPermissionOptions,
  UnselectedMessageMedium,
} from './common/types';
import { useGetMessagingPermission } from './common/utils';
import { useEditableFieldsContext } from './context/EditableFieldsContext';
import { useQueryParamContext } from './context/QueryParamContext';
import { useTemplateQueryContext } from './context/TemplateQueryContext';
import { useSendTestMsgContext } from './context/SendTestMsgContext';
import { EditEmail } from './EditEmail/EditEmail';
import { EditInApp } from './EditInApp/EditInApp';
import { EditPush } from './EditPush/EditPush';
import { EditSMS } from './EditSMS/EditSMS';
import { EditWebPush } from './EditWebPush/EditWebPush';
import { EditEmbedded } from './EditEmbedded/EditEmbedded';
import { EditWhatsApp } from './EditWhatsApp/EditWhatsApp';
import { UnsavedChangesPrompts } from './modals/UnsavedChangesPrompts';
import { SendTestMsgModals } from './modals';

// includes props from react-router (history, location, match)
export interface TemplateEditIndexProps extends RouteComponentProps<any> {
  content: DisplayContent;
}

const TEMPLATE_EDITOR_MEDIUM_COMPONENT_MAPPER = {
  [MessageMedium.Email]: EditEmail,
  [MessageMedium.InApp]: EditInApp,
  [MessageMedium.Push]: EditPush,
  [MessageMedium.SMS]: EditSMS,
  [MessageMedium.WebPush]: EditWebPush,
  [EmbeddedMessageMedium.Embedded]: EditorLoader,
  [WhatsAppMedium.WhatsApp]: EditorLoader,
  [UnselectedMessageMedium.Unselected]: EditorLoader,
} as const;

export const TemplateEditIndexPage: FC<React.PropsWithChildren<TemplateEditIndexProps>> = () => {
  const isEmbeddedEnabled = useSelector(selectIsEmbeddedMessagingCoreFeatureFlagOn);
  const { isWhatsAppEnabled: isWhatsAppFFOn } = useWhatsAppMessageChannelFeature();
  const { errorMessage, hasError, isLoading, medium } = useTemplateQueryContext();
  const { liveTemplate } = useEditableFieldsContext();
  const { onFetchLists } = useSendTestMsgContext();
  const { campaignId, experimentFlow: isExperimentFlow } = useQueryParamContext();
  const messagingPermission = useGetMessagingPermission();
  const shouldBlockContent =
    (!!campaignId || isExperimentFlow) && messagingPermission === MessagingPermissionOptions.NONE;
  const { templateId } = liveTemplate;
  const query = useQueryParams();
  // TODO: Remove this when data is available
  const showMockTemplateDetails = query.get('showMockWhatsAppTemplate');

  const getComponent = useCallback(() => {
    if (shouldBlockContent) {
      return TemplateViewPermissionError;
    }

    if (hasError && !isLoading) {
      if (errorMessage === GqlTemplateResponseTypes.CORRUPTED_TEMPLATE_DATA) {
        return TemplateCorruptedError;
      }
      return TemplateNotFoundError;
    }

    // important: only show the loader on the initial load,
    // (otherwise the child component will unmount/remount during a refetch)
    if ((isLoading && !medium) || !templateId) {
      return EditorLoader;
    }

    if (medium === EmbeddedMessage && isEmbeddedEnabled) {
      return EditEmbedded;
    }

    if ((showMockTemplateDetails || medium === WhatsApp) && isWhatsAppFFOn) {
      return EditWhatsApp;
    }

    return TEMPLATE_EDITOR_MEDIUM_COMPONENT_MAPPER[medium] || EditorLoader;
  }, [hasError, isEmbeddedEnabled, isLoading, medium, shouldBlockContent, templateId]);

  const TemplateEditor = getComponent();

  useEffect(() => onFetchLists(), []);

  return (
    <>
      <TemplateEditor />
      <UnsavedChangesPrompts />
      <SendTestMsgModals />
    </>
  );
};
